/**
 * Node Geometries View Component
 * Handles search, selection, and loading of node geometries and hierarchies
 */

import React, { useState, useEffect, useCallback } from 'react';
import type { FC } from 'react';
import { useDispatch } from 'react-redux';
import type { Dispatch } from 'redux';
import store from '../../../../store';
import { wrapTo, addDataToMap, addNotification } from '@kepler.gl/actions';
import { processGeojson } from '@kepler.gl/processors';
import styled from 'styled-components';
import type {
  ResourceViewProps,
  GridAssetNodeSummaryDto,
  NodeSelectionEntry,
  LoadProgressSnapshot,
  LoadResult,
} from '../../types';
import { NodeSelectionManager } from '../../selection/node-selection-manager';
import { NodeLoadManager } from '../../loading/node-load-manager';
import { convertMultipleNodesToGeoJSON } from '../../utils/geojson-converter';
import { NodeSearchResults } from '../NodeSearchResults';
import { SelectionList } from '../SelectionList';
import { LoadingProgress } from '../LoadingProgress';

// Layer name constant for Kepler.gl dataset creation
const LAYER_NAME = 'Hierarchy Coverage';

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Generates a unique dataset name by checking existing datasets and incrementing
 *
 * Note: Kepler.gl's internal dataset structure uses {id, label, color, fields, allData, ...}
 * but we only need to check labels for uniqueness when creating new datasets
 */
function generateUniqueDatasetName(
  baseName: string,
  existingDatasets: Record<string, { label?: string }>
): string {
  const existingLabels = Object.values(existingDatasets).map(ds => ds.label || '');

  // If base name doesn't exist, use it
  if (!existingLabels.includes(baseName)) {
    return baseName;
  }

  // Find the next available number
  let counter = 2;
  while (existingLabels.includes(`${baseName} ${counter}`)) {
    counter++;
  }

  return `${baseName} ${counter}`;
}

/**
 * Separates load results into successful and failed results
 */
function separateLoadResults(results: LoadResult[]) {
  return {
    successful: results.filter(r => r.success && r.features && r.features.length > 0),
    failed: results.filter(r => !r.success),
  };
}

/**
 * Flattens load results to include parent nodes and all descendants
 */
function flattenLoadResults(results: LoadResult[]) {
  return results.flatMap(r => [
    // Parent node
    {
      lsid: r.lsid,
      name: r.name,
      nodeType: r.nodeType,
      externalId: r.externalId,
      features: r.features!,
    },
    // Descendant nodes (if any)
    ...(r.descendantNodes || []),
  ]);
}

/**
 * Creates a unique dataset ID for LoadSEER data
 */
function createDatasetId(): string {
  return `loadseer-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
}

// ============================================================================
// Styled Components
// ============================================================================

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const SectionTitle = styled.h3`
  font-size: 1rem;
  font-weight: 600;
  color: ${props => props.theme?.textColor || '#e6edf3'};
  margin: 0;
  padding-bottom: 0.5rem;
  border-bottom: 1px solid ${props => props.theme?.borderColor || '#30363d'};
`;

const LoadButton = styled.button`
  padding: 0.75rem 1.5rem;
  background: ${props => props.theme?.buttonBgd || '#238636'};
  color: #ffffff;
  border: none;
  border-radius: 4px;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s;

  &:hover:not(:disabled) {
    background: ${props => props.theme?.buttonBgdHover || '#2ea043'};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const ErrorMessage = styled.div`
  padding: 0.75rem;
  background: ${props => props.theme?.errorColor || '#f85149'}22;
  color: ${props => props.theme?.errorColor || '#f85149'};
  border: 1px solid ${props => props.theme?.errorColor || '#f85149'}66;
  border-radius: 4px;
  font-size: 0.875rem;
`;

// ============================================================================
// Component
// ============================================================================

export const NodeGeometriesView: FC<ResourceViewProps> = ({
  apiClient,
  nodeTypes,
  isLoading,
  setIsLoading,
  onLoadComplete,
}) => {
  const dispatch = useDispatch<Dispatch>();

  // Search state
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<GridAssetNodeSummaryDto[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);

  // Selection state
  const [selectionManager] = useState(() => new NodeSelectionManager());
  const [selectedLsids, setSelectedLsids] = useState<Set<string>>(new Set());
  const [descendantStates, setDescendantStates] = useState<
    Map<string, { enabled: boolean; nodeType?: string }>
  >(new Map());

  // Loading state
  const [loadManager, setLoadManager] = useState<NodeLoadManager | null>(null);
  const [loadProgress, setLoadProgress] = useState<LoadProgressSnapshot | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);

  // Handle clear all selections
  const handleClearAll = useCallback(() => {
    selectionManager.clear();
    setSelectedLsids(new Set());
    setDescendantStates(new Map());
  }, [selectionManager]);

  // Initialize load manager when API client changes
  useEffect(() => {
    const manager = new NodeLoadManager(apiClient);

    // Subscribe to progress updates
    const unsubscribeProgress = manager.subscribeProgress(progress => {
      setLoadProgress(progress);
    });

    // Subscribe to completion
    const unsubscribeComplete = manager.subscribeComplete(event => {
      setIsLoading(false);

      const { results } = event;
      const { successful, failed } = separateLoadResults(results);

      if (successful.length === 0) {
        // All failed
        const errorMsg =
          failed.length > 0
            ? `Failed to load ${failed.length} node${failed.length !== 1 ? 's' : ''}`
            : 'No geometries found';
        setLoadError(errorMsg);
        (dispatch as any)(
          wrapTo(
            'map',
            addNotification({
              type: 'error',
              message: errorMsg,
            })
          )
        );
        return;
      }

      // Convert to GeoJSON and ingest via Kepler.gl standard pattern
      try {
        const allNodes = flattenLoadResults(successful);
        const geojson = convertMultipleNodesToGeoJSON(allNodes);

        // Create layer name based on loaded nodes
        const baseName = LAYER_NAME;

        // Get current datasets state at load time (not from closure)
        // NOTE: Using store.getState() here instead of useSelector because we're inside
        //       an async callback and need fresh state, not stale closure values.
        //       This is intentional and follows the pattern for async Redux operations.
        const currentState = store.getState();
        const currentDatasets = (currentState.keplerGl as any)?.map?.visState?.datasets || {};

        // Generate unique name to avoid replacing existing datasets
        const layerName = generateUniqueDatasetName(baseName, currentDatasets);

        // Generate unique dataset ID (timestamp + random for guaranteed uniqueness)
        const datasetId = createDatasetId();

        if (import.meta.env.DEV) {
          console.log('Creating dataset:', {
            id: datasetId,
            label: layerName,
            existingDatasets: Object.keys(currentDatasets),
          });
        }

        // Process GeoJSON to Kepler.gl format {fields, rows}
        const processedData = processGeojson(geojson);

        if (!processedData) {
          (dispatch as any)(
            addNotification({
              type: 'error',
              message: 'Failed to process GeoJSON data',
            })
          );
          return;
        }

        // Use addDataToMap with proper dataset structure
        (dispatch as any)(
          wrapTo(
            'map',
            addDataToMap({
              datasets: {
                info: {
                  id: datasetId,
                  label: layerName,
                },
                data: processedData,
              },
              options: {
                centerMap: true,
                readOnly: false,
              },
            })
          )
        );

        // Count total failures (parent nodes + descendants)
        const totalFailedDescendants = successful.reduce(
          (sum, r) => sum + (r.failedDescendants?.length || 0),
          0
        );
        const hasFailures = failed.length > 0 || totalFailedDescendants > 0;

        // Show success/warning notification
        let message = '';
        if (hasFailures) {
          const parts: string[] = [];
          if (successful.length > 0) {
            parts.push(`Loaded ${successful.length} node${successful.length !== 1 ? 's' : ''}`);
          }
          if (failed.length > 0) {
            parts.push(`${failed.length} node${failed.length !== 1 ? 's' : ''} failed`);
          }
          if (totalFailedDescendants > 0) {
            parts.push(
              `${totalFailedDescendants} descendant${totalFailedDescendants !== 1 ? 's' : ''} failed`
            );
          }
          message = parts.join(', ');
        } else {
          message = `Loaded ${successful.length} node${successful.length !== 1 ? 's' : ''} successfully`;
        }

        (dispatch as any)(
          wrapTo(
            'map',
            addNotification({
              type: hasFailures ? 'warning' : 'success',
              message,
            })
          )
        );

        // Show individual error notifications for failed nodes
        failed.forEach(result => {
          (dispatch as any)(
            wrapTo(
              'map',
              addNotification({
                type: 'error',
                message: `Failed to load ${result.name}: ${result.error}`,
              })
            )
          );
        });

        // Show individual error notifications for failed descendants
        successful.forEach(result => {
          result.failedDescendants?.forEach(desc => {
            (dispatch as any)(
              wrapTo(
                'map',
                addNotification({
                  type: 'error',
                  message: `Failed to load descendant ${desc.name} (parent: ${result.name}): ${desc.error}`,
                })
              )
            );
          });
        });

        // Clear selections only on complete success (no failures)
        if (!hasFailures) {
          handleClearAll();
        }

        onLoadComplete();
      } catch (error) {
        const errorMsg = error instanceof Error ? error.message : 'Failed to process geometries';
        setLoadError(errorMsg);
        (dispatch as any)(
          wrapTo(
            'map',
            addNotification({
              type: 'error',
              message: errorMsg,
            })
          )
        );
      }
    });

    setLoadManager(manager);

    // Cleanup subscriptions on unmount or when dependencies change
    return () => {
      unsubscribeProgress();
      unsubscribeComplete();
    };
  }, [
    apiClient,
    dispatch,
    handleClearAll,
    nodeTypes,
    selectionManager,
    setIsLoading,
    onLoadComplete,
  ]);

  // Handle search
  const handleSearch = useCallback(async () => {
    if (!searchQuery.trim()) return;

    setIsSearching(true);
    setSearchError(null);

    try {
      const response = await apiClient.searchNodes({ query: searchQuery.trim() });
      setSearchResults(response.results);
    } catch (error) {
      setSearchError(error instanceof Error ? error.message : 'Search failed');
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  }, [apiClient, searchQuery]);

  // Handle node selection toggle
  const handleToggleSelection = useCallback(
    (node: GridAssetNodeSummaryDto) => {
      const entry: NodeSelectionEntry = {
        lsid: node.lsid,
        name: node.name,
        nodeType: node.nodeType,
        externalId: node.externalId,
        includeDescendants: false,
      };
      const result = selectionManager.toggle(entry);
      if (result.added || !result.added) {
        setSelectedLsids(new Set(Array.from(selectionManager.list()).map(e => e.lsid)));
      }
    },
    [selectionManager]
  );

  // Handle descendant toggle
  const handleToggleDescendants = useCallback(
    (lsid: string, enabled: boolean) => {
      selectionManager.update(lsid, { includeDescendants: enabled });
      setDescendantStates(
        new Map(
          Array.from(selectionManager.list()).map(e => [
            e.lsid,
            { enabled: e.includeDescendants, nodeType: e.descendantNodeType },
          ])
        )
      );
    },
    [selectionManager]
  );

  // Handle descendant node type filter
  const handleDescendantTypeChange = useCallback(
    (lsid: string, nodeType: string | undefined) => {
      selectionManager.update(lsid, { descendantNodeType: nodeType });
      setDescendantStates(
        new Map(
          Array.from(selectionManager.list()).map(e => [
            e.lsid,
            { enabled: e.includeDescendants, nodeType: e.descendantNodeType },
          ])
        )
      );
    },
    [selectionManager]
  );

  // Handle remove from selection
  const handleRemove = useCallback(
    (lsid: string) => {
      selectionManager.remove(lsid);
      setSelectedLsids(new Set(Array.from(selectionManager.list()).map(e => e.lsid)));
      setDescendantStates(
        new Map(
          Array.from(selectionManager.list()).map(e => [e.lsid, { enabled: e.includeDescendants }])
        )
      );
    },
    [selectionManager]
  );

  // Handle load nodes
  const handleLoadNodes = useCallback(async () => {
    if (!loadManager || isLoading) return;

    const selections = selectionManager.list();
    if (selections.length === 0) return;

    setIsLoading(true);
    setLoadError(null);
    setLoadProgress(null);

    try {
      const results = await loadManager.loadNodes(selections);
      if (import.meta.env.DEV) {
        console.log('Load results:', results);
      }

      // Check for any failures
      const failures = results.filter(r => !r.success);
      if (failures.length > 0) {
        setLoadError(`${failures.length} node(s) failed to load`);
      }
    } catch (error) {
      setLoadError(error instanceof Error ? error.message : 'Load failed');
      setIsLoading(false);
    }
  }, [loadManager, selectionManager, isLoading, setIsLoading]);

  // Handle cancel loading
  const handleCancelLoad = useCallback(() => {
    if (loadManager) {
      loadManager.cancel();
      setIsLoading(false);
      setLoadProgress(null);
    }
  }, [loadManager, setIsLoading]);

  return (
    <Container>
      <Section>
        <SectionTitle>Search Nodes</SectionTitle>
        <NodeSearchResults
          results={searchResults}
          selectedLsids={selectedLsids}
          onToggleSelection={handleToggleSelection}
          onToggleDescendants={handleToggleDescendants}
          onDescendantTypeChange={handleDescendantTypeChange}
          descendantStates={descendantStates}
          isLoading={isSearching}
          nodeTypes={nodeTypes}
          searchQuery={searchQuery}
          onSearchQueryChange={setSearchQuery}
          onSearch={handleSearch}
        />
        {searchError && <ErrorMessage>{searchError}</ErrorMessage>}
      </Section>

      <Section>
        <SectionTitle>Selected Nodes</SectionTitle>
        <SelectionList
          selections={selectionManager.list()}
          maxSelections={20}
          onRemove={handleRemove}
          onClearAll={handleClearAll}
          nodeTypes={nodeTypes}
        />
      </Section>

      {selectedLsids.size > 0 && (
        <Section>
          <LoadButton
            onClick={handleLoadNodes}
            disabled={isLoading || selectedLsids.size === 0}
            data-testid='loadseer-load-button'
          >
            {isLoading
              ? 'Loading...'
              : `Load ${selectedLsids.size} Node${selectedLsids.size !== 1 ? 's' : ''}`}
          </LoadButton>
          {loadError && <ErrorMessage>{loadError}</ErrorMessage>}
        </Section>
      )}

      {loadProgress && (
        <Section>
          <LoadingProgress
            progress={loadProgress}
            onCancel={isLoading ? handleCancelLoad : () => {}}
            data-testid='loadseer-loading-progress'
          />
        </Section>
      )}
    </Container>
  );
};
